import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Users } from 'lucide-react';
import { Layout } from '../components/Layout'; 
import { InnerCircleList } from '../components/InnerCircle/InnerCircleList';
import { AddContactForm } from '../components/InnerCircle/AddContactForm';
import { GroupList } from '../components/InnerCircle/GroupList';
import { CreateGroupModal } from '../components/InnerCircle/CreateGroupModal';
import { GroupChat } from '../components/InnerCircle/GroupChat';
import { SharedItems } from '../components/InnerCircle/SharedItems';
import { MemberList } from '../components/InnerCircle/MemberList';
import { useInnerCircle } from '../hooks/useInnerCircle';
import { useInnerCircleGroups } from '../hooks/useInnerCircleGroups';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradePrompt } from '../components/Premium/UpgradePrompt';

export function InnerCircle() {
  const { hasFeature, showUpgradePrompt } = useSubscription();
  const { contacts, loading, addContact, updateContactStatus } = useInnerCircle();
  const { groups, createGroup, inviteMember, shareItem, sendMessage } = useInnerCircleGroups();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const isPremium = hasFeature('inner_circle');

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <UpgradePrompt
            feature="Inner Circle"
            description="Build your trusted network with our private community feature. Connect with collaborators and share resources securely."
            onUpgrade={() => showUpgradePrompt('inner_circle')}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 lg:flex lg:gap-6">
        <div className="lg:w-1/3 space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Inner Circle</h1>
            <p className="text-gray-400">Manage your trusted contacts network</p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#bbdde5] text-black rounded-xl hover:opacity-90 transition-opacity"
            >
              <Plus className="w-5 h-5" />
              Add Contact
            </button>
            <button
              onClick={() => setShowCreateGroup(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#bbdde5] text-black rounded-xl hover:opacity-90 transition-opacity"
            >
              <Users className="w-5 h-5" />
              New Group
            </button>
          </div>

          <GroupList
            groups={groups}
            selectedGroup={selectedGroup}
            onGroupSelect={setSelectedGroup}
            onCreateGroup={() => setShowCreateGroup(true)}
            onManageGroup={() => {}}
          />
        </div>

        <div className="lg:flex-1">
          {selectedGroup ? (
            <div className="space-y-6">
              <GroupChat
                group={groups.find(g => g.id === selectedGroup)!}
                messages={[]}
                onSendMessage={content => sendMessage(selectedGroup, content)}
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SharedItems items={[]} onViewItem={() => {}} />
                <MemberList
                  members={[]}
                  onInviteMember={() => {}}
                  onUpdateRole={() => {}}
                  onRemoveMember={() => {}}
                  isAdmin={true}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              Select a group to view details
            </div>
          )}
        </div>
      </div>

      {showAddForm && (
        <AddContactForm
          onSubmit={async (email, notes) => {
            try {
              await addContact(email, notes);
              setShowAddForm(false);
            } catch (error) {
              console.error('Error adding contact:', error);
            }
          }}
          onClose={() => setShowAddForm(false)}
        />
      )}
      
      {showCreateGroup && (
        <CreateGroupModal
          onSubmit={async (data) => {
            try {
              await createGroup(data);
              setShowCreateGroup(false);
            } catch (error) {
              console.error('Error creating group:', error);
            }
          }}
          onClose={() => setShowCreateGroup(false)}
        />
      )}
    </Layout>
  );
}